import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { useAddNewRoleMutation } from '../../api/rolesApiSlice';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';

function RoleList({ roles, handleDeleteRooli, handleUpdateRooli, fetchData, onSuccess, onError }) {
    const [selectedRole, setSelectedRole] = useState(null);
    const [newRoleData, setNewRoleData] = useState({ rooli: '', kuvaus: '' });
    const [addRoleData, setAddRoleData] = useState({ rooli: '', kuvaus: '' });
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10;
    const [addNewRole, { isLoading }] = useAddNewRoleMutation();

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredRoles = Array.isArray(roles)
        ? roles.filter((role) =>
            role.rooli.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];
    const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedRoles = filteredRoles.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (role) => {
        setShowAddForm(false);
        if (selectedRole?.id === role.id) {
            setSelectedRole(null);
            setNewRoleData({ rooli: '', kuvaus: '' });
        } else {
            setSelectedRole(role);
            setNewRoleData(role);
        }
    };

    const handleAddClick = () => {
        setSelectedRole(null);
        setNewRoleData({ rooli: '', kuvaus: '' });
        setShowAddForm(!showAddForm);
    };

    const handleLisääRooli = async (role) => {
        try {
            const result = await addNewRole(role);
            if (result.error) onError('Tehtävän luominen epäonnistui');
            if (!result.error) onSuccess('Tehtävän luominen onnistui.', 'roolit', result.data.id);
            setAddRoleData({ rooli: '', kuvaus: '' });
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            console.error(error);
            onError('Tehtävän luominen epäonnistui');
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Roolit
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {/* Roles List */}
                {displayedRoles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei rooleja näytettäväksi.
                    </Typography>
                ) : (
                    displayedRoles.map((role) => {
                        const isSelected = selectedRole?.id === role.id;

                        return (
                            <Box key={role.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(role)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                                            {role.rooli}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {role.kuvaus || 'Ei kuvausta lisätty'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteRooli(event, role);
                                            if (selectedRole?.id === role.id) {
                                                setSelectedRole(null);
                                                setNewRoleData({ rooli: '', kuvaus: '' });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa roolia
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8}>
                                                <FormTextField
                                                    fieldName="rooli"
                                                    label="Rooli"
                                                    multiline
                                                    fullWidth
                                                    value={newRoleData.rooli}
                                                    onChange={(event) =>
                                                        setNewRoleData((prev) => ({ ...prev, rooli: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormTextField
                                                    fieldName="kuvaus"
                                                    label="Kuvaus"
                                                    multiline
                                                    fullWidth
                                                    value={newRoleData.kuvaus}
                                                    onChange={(event) =>
                                                        setNewRoleData((prev) => ({ ...prev, kuvaus: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <UpdateButton handleUpdate={handleUpdateRooli} data={newRoleData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Add Form */}
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää rooli'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi rooli
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    <FormTextField
                                        key="addRoleRooli"
                                        fieldName="rooli"
                                        label="Rooli"
                                        multiline
                                        fullWidth
                                        value={addRoleData.rooli}
                                        onChange={(event) =>
                                            setAddRoleData((prev) => ({ ...prev, rooli: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormTextField
                                        key="addRoleKuvaus"
                                        fieldName="kuvaus"
                                        label="Kuvaus"
                                        multiline
                                        fullWidth
                                        value={addRoleData.kuvaus}
                                        onChange={(event) =>
                                            setAddRoleData((prev) => ({ ...prev, kuvaus: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isLoading ? null : <AddIcon />}
                                        onClick={() => handleLisääRooli(addRoleData)}
                                        disabled={isLoading}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Lisää'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {filteredRoles.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredRoles}
                            array={roles}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default RoleList;